import React, { useEffect, useState } from "react";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface NetworkTypeFormProps {
  mode: "add" | "edit";
  formData: { name: string; description: string };
  onFormChange: (data: { name: string; description: string }) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function NetworkTypeForm({
  mode,
  formData,
  onFormChange,
  onSave,
  onCancel,
}: NetworkTypeFormProps) {
  const [localFormData, setLocalFormData] = useState({
    name: formData.name || "",
    description: formData.description || "",
  });

  // Update local form data when props change
  useEffect(() => {
    setLocalFormData({
      name: formData.name || "",
      description: formData.description || "",
    });
  }, [formData]);

  // Form field definitions
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Network Type Name",
      placeholder: "Enter network type name",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Enter network type description",
      rows: 3,
      required: false,
      className: "col-span-1 mt-3",
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: localFormData,
  });

  // Reset form when localFormData changes
  useEffect(() => {
    form.reset(localFormData);
  }, [localFormData, form]);

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    const updatedData = {
      ...localFormData,
      [fieldName]: value,
    };
    setLocalFormData(updatedData);
    onFormChange(updatedData);
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!localFormData.name.trim()) {
      return;
    }
    onSave();
  };

  // Handle form reset/cancel
  const handleReset = () => {
    onCancel();
  };

  const title = mode === "add" ? "Add New Network Type" : "Edit Network Type";
  const saveButtonText =
    mode === "add" ? "Add Network Type" : "Update Network Type";

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {mode === "add"
            ? "Create a new network type with name and description"
            : "Update the network type details"}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onFieldChange={handleFieldChange}
          gridColumns={1}
          responsiveColumns={{
            sm: 1,
            md: 1,
          }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: saveButtonText,
            resetButtonText: "Cancel",
            spacing: "comfortable",
            classes: {
              form: "space-y-6",
              button: "px-4 py-2 rounded-md font-medium transition-colors",
              buttonContainer: "flex gap-2 justify-end pt-6 mt-8",
              input:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
              textarea:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
              select:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
              label: "block text-sm font-medium text-gray-700 mb-1",
              error: "text-sm text-red-600 mt-1",
              container: "space-y-1",
            },
          }}
        />
      </div>
    </>
  );
}
